import { Avatar, AvatarFallback, AvatarImage } from '@crowdfpl/ui-web/avatar';
import { Badge } from '@crowdfpl/ui-web/badge';
import { Card, CardContent } from '@crowdfpl/ui-web/card';
import { Skeleton } from '@crowdfpl/ui-web/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@crowdfpl/ui-web/tabs';
import { Loader2 } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { PinnedTrophyBadge } from '@/components/gamification/pinned-trophy-badge';
import { UsernameWithXLink } from '@/components/shared/UsernameWithXLink';
import { useAuth } from '@/hooks/auth';
import { useInfiniteDuelVotesForPlayer } from '@/hooks/data/duelVotes';
import { useIsMobile } from '@/hooks/utils/use-mobile';
import type { DuelVoteDto } from '@/types/duelVotes';

interface Props {
  duelId: string;
  playerAId: string;
  playerBId: string;
  rawPlayerAId: string;
  rawPlayerBId: string;
  playerAName: string;
  playerBName: string;
  aPct: number;
  bPct: number;
}

const getRankIcon = (rank: number) => (rank === 1 ? '🥇' : rank === 2 ? '🥈' : rank === 3 ? '🥉' : null);
const getRankColor = (rank: number) =>
  rank === 1 ? 'text-yellow-600 dark:text-yellow-500' : rank === 2 ? 'text-gray-400 dark:text-gray-500' : rank === 3 ? 'text-amber-600 dark:text-amber-500' : 'text-muted-foreground/60';

function VoterCard({ voter, isMobile, currentUserId }: { voter: DuelVoteDto; isMobile: boolean; currentUserId?: string }) {
  const isCurrentUser = currentUserId === voter.user_id;
  const rank = voter.global_rank ?? null;
  const showRank = rank != null && rank > 0;

  // If no username, render as div (can't link to profile)
  if (!voter.username) {
    return (
      <div
        className={`flex items-center gap-3 ${isMobile ? 'p-2' : 'p-3'} rounded-lg border transition-colors ${isCurrentUser ? 'bg-gradient-to-r from-primary/5 to-primary/10 border-l-4 border-l-primary' : 'bg-muted/50 border-transparent'}`}
        aria-label={`User ${voter.user_id}`}
      >
        {showRank && (
          <div className={`${isMobile ? 'w-8' : 'w-10'} text-center flex-shrink-0`}>
            {getRankIcon(rank) ? (
              <span className={isMobile ? 'text-base' : 'text-lg'}>{getRankIcon(rank)}</span>
            ) : (
              <span className={`${isMobile ? 'text-xs' : 'text-sm'} font-medium leading-tight ${getRankColor(rank)}`}>#{rank}</span>
            )}
          </div>
        )}
        <Avatar className={isMobile ? 'h-8 w-8' : 'h-10 w-10'}>
          <AvatarImage src={voter.avatar_url || undefined} />
          <AvatarFallback className="text-sm">UN</AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <div className={`font-medium ${isMobile ? 'text-sm' : 'text-base'} truncate`}>{voter.user_id}</div>
        </div>
        <Badge variant="outline" className={isMobile ? 'text-[10px] px-1 py-0' : 'text-xs'} aria-label="Rating">
          {voter.predictor_rating ?? 0}
        </Badge>
      </div>
    );
  }

  return (
    <Link
      to={`/profile/${voter.username}`}
      className={`flex items-center gap-3 ${isMobile ? 'p-2' : 'p-3'} rounded-lg border transition-colors ${isCurrentUser ? 'bg-gradient-to-r from-primary/5 to-primary/10 border-l-4 border-l-primary' : 'bg-muted/50 hover:bg-muted/80 border-transparent'}`}
      aria-label={`View profile of ${voter.username}`}
    >
      {showRank && (
        <div className={`${isMobile ? 'w-8' : 'w-10'} text-center flex-shrink-0`}>
          {getRankIcon(rank) ? (
            <span className={isMobile ? 'text-base' : 'text-lg'}>{getRankIcon(rank)}</span>
          ) : (
            <span className={`${isMobile ? 'text-xs' : 'text-sm'} font-medium leading-tight ${getRankColor(rank)}`}>#{rank}</span>
          )}
        </div>
      )}
      <Avatar className={isMobile ? 'h-8 w-8' : 'h-10 w-10'}>
        <AvatarImage src={voter.avatar_url || undefined} />
        <AvatarFallback className="text-sm">{voter.username.slice(0, 2).toUpperCase()}</AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <div className={`flex min-w-0 items-center gap-1.5 font-medium ${isMobile ? 'text-sm' : 'text-base'}`}>
          <div className="min-w-0 truncate">
            <UsernameWithXLink
              username={voter.username}
              twitterUsername={voter.twitter_username}
              showTwitterUsername={voter.show_twitter_username}
              iconClassName={isMobile ? 'h-3 w-3' : 'h-3.5 w-3.5'}
              insideLink
              trackSource="duel_voters"
            />
          </div>
          <PinnedTrophyBadge pinnedTrophy={voter.pinned_trophy} decorative />
        </div>
      </div>
      <Badge variant="outline" className={isMobile ? 'text-[10px] px-1 py-0' : 'text-xs'} aria-label="Rating">
        {voter.predictor_rating ?? 0}
      </Badge>
    </Link>
  );
}

interface VotersListProps {
  votes: DuelVoteDto[];
  isLoading: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  error: unknown;
  emptyText: string;
  isMobile: boolean;
  currentUserId?: string;
}

function VotersList({ votes, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage, error, emptyText, isMobile, currentUserId }: VotersListProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    const loadMoreEl = loadMoreRef.current;
    if (!container || !loadMoreEl || !hasNextPage || isFetchingNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      {
        root: container,
        threshold: 0.1,
        rootMargin: '50px',
      },
    );

    observer.observe(loadMoreEl);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const LoadingList = ({ count = 5 }: { count?: number }) => (
    <div className={isMobile ? 'space-y-2' : 'space-y-3'}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="flex items-center gap-3">
          <Skeleton className={isMobile ? 'h-8 w-8 rounded-full' : 'h-10 w-10 rounded-full'} />
          <div className="flex-1 min-w-0">
            <Skeleton className={isMobile ? 'h-3 w-2/3' : 'h-4 w-1/2'} />
          </div>
          <Skeleton className={isMobile ? 'h-5 w-10' : 'h-6 w-12'} />
        </div>
      ))}
    </div>
  );

  if (isLoading) {
    return <LoadingList count={isMobile ? 5 : 8} />;
  }

  if (error) {
    return <div className="text-center py-4 text-destructive">Failed to load votes</div>;
  }

  if (votes.length === 0) {
    return (
      <div className={isMobile ? 'text-center py-4 text-muted-foreground' : 'text-center py-8 text-muted-foreground'}>
        {emptyText}
      </div>
    );
  }

  // Calculate max height based on device
  const maxHeight = isMobile ? 'max-h-[280px]' : 'max-h-[400px]';

  return (
    <div
      ref={containerRef}
      className={`${maxHeight} overflow-y-auto ${isMobile ? 'space-y-2 pr-1' : 'space-y-3 pr-2'} scrollbar-thin scrollbar-thumb-muted-foreground/20 scrollbar-track-transparent`}
    >
      {votes.map((voter) => (
        <VoterCard key={voter.id} voter={voter} isMobile={isMobile} currentUserId={currentUserId} />
      ))}
      {hasNextPage && (
        <div ref={loadMoreRef} className="py-3 flex justify-center">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-xs">Loading...</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default function VotersSectionContainer({ duelId, playerAId, playerBId, rawPlayerAId, rawPlayerBId, playerAName, playerBName, aPct, bPct }: Props) {
  const isMobile = useIsMobile();
  const { user } = useAuth();

  // Resolve player IDs (prefer enriched ID, fallback to raw, filter out empty strings)
  const resolvedPlayerAId = (playerAId && playerAId.length > 0) ? playerAId : rawPlayerAId;
  const resolvedPlayerBId = (playerBId && playerBId.length > 0) ? playerBId : rawPlayerBId;

  // Separate infinite queries for each player
  const playerAQuery = useInfiniteDuelVotesForPlayer({
    duelId,
    playerId: resolvedPlayerAId,
    order: 'predictor_rating.desc',
    limit: 20,
  });

  const playerBQuery = useInfiniteDuelVotesForPlayer({
    duelId,
    playerId: resolvedPlayerBId,
    order: 'predictor_rating.desc',
    limit: 20,
  });

  if (isMobile) {
    return (
      <Card>
        <CardContent className="px-4 pb-4 pt-4">
          <Tabs defaultValue="playerA" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-4 h-auto p-1">
              <TabsTrigger value="playerA" className="text-xs w-full h-7 py-0">
                {playerAName} ({playerAQuery.totalCount})
              </TabsTrigger>
              <TabsTrigger value="playerB" className="text-xs w-full h-7 py-0">
                {playerBName} ({playerBQuery.totalCount})
              </TabsTrigger>
            </TabsList>
            <TabsContent value="playerA">
              <VotersList
                votes={playerAQuery.votes}
                isLoading={playerAQuery.isLoading}
                isFetchingNextPage={playerAQuery.isFetchingNextPage}
                hasNextPage={!!playerAQuery.hasNextPage}
                fetchNextPage={playerAQuery.fetchNextPage}
                error={playerAQuery.error}
                emptyText="No votes yet"
                isMobile={isMobile}
                currentUserId={user?.id}
              />
            </TabsContent>
            <TabsContent value="playerB">
              <VotersList
                votes={playerBQuery.votes}
                isLoading={playerBQuery.isLoading}
                isFetchingNextPage={playerBQuery.isFetchingNextPage}
                hasNextPage={!!playerBQuery.hasNextPage}
                fetchNextPage={playerBQuery.fetchNextPage}
                error={playerBQuery.error}
                emptyText="No votes yet"
                isMobile={isMobile}
                currentUserId={user?.id}
              />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between pb-3">
            <div className="flex items-center gap-2">
              <div className="text-lg font-semibold">{playerAName} ({playerAQuery.totalCount})</div>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-8 bg-muted rounded-full overflow-hidden">
                <div className="w-full bg-primary rounded-full transition-all duration-500" style={{ height: `${aPct}%`, opacity: (aPct / 100) * 0.8 + 0.2 }} />
              </div>
              <span className="text-xl font-bold text-primary">{aPct}%</span>
            </div>
          </div>
          <VotersList
            votes={playerAQuery.votes}
            isLoading={playerAQuery.isLoading}
            isFetchingNextPage={playerAQuery.isFetchingNextPage}
            hasNextPage={!!playerAQuery.hasNextPage}
            fetchNextPage={playerAQuery.fetchNextPage}
            error={playerAQuery.error}
            emptyText="No votes yet"
            isMobile={isMobile}
            currentUserId={user?.id}
          />
        </CardContent>
      </Card>
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between pb-3">
            <div className="flex items-center gap-2">
              <div className="text-lg font-semibold">{playerBName} ({playerBQuery.totalCount})</div>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-8 bg-muted rounded-full overflow-hidden">
                <div className="w-full bg-primary rounded-full transition-all duration-500" style={{ height: `${bPct}%`, opacity: (bPct / 100) * 0.8 + 0.2 }} />
              </div>
              <span className="text-xl font-bold text-primary">{bPct}%</span>
            </div>
          </div>
          <VotersList
            votes={playerBQuery.votes}
            isLoading={playerBQuery.isLoading}
            isFetchingNextPage={playerBQuery.isFetchingNextPage}
            hasNextPage={!!playerBQuery.hasNextPage}
            fetchNextPage={playerBQuery.fetchNextPage}
            error={playerBQuery.error}
            emptyText="No votes yet"
            isMobile={isMobile}
            currentUserId={user?.id}
          />
        </CardContent>
      </Card>
    </div>
  );
}
